/**
 * Robots API Help — pagina di documentazione per gli sviluppatori che integrano
 * i robot nella piattaforma Flowenti (15 endpoint REST, gestione errori,
 * examples code hub in Python/TypeScript/Delphi).
 *
 * Material Design 3, dark/light automatico via `useTheme()`. Il toggle in
 * Dashboard swappa istantaneamente lo schema colori senza cablaggi custom.
 *
 * Tutte le stringhe visibili arrivano da `useTranslations()` con fallback al
 * dict locale `robotsHelpDict.ts` (6 lingue: it, en, fr, de, es, pt).
 *
 * Feature interattive (vedi `robots-help-design-system.md`):
 *   - Filter chips cliccabili per categoria
 *   - Try Call simulate (idle → spinner → ✓ 200 OK → reset)
 *   - Copy buttons ovunque (path, base URL, auth header) → snackbar globale
 *   - JSON-LD TechArticle iniettato nel <head>
 *
 * NOTA 2026-09-07: la global search con Cmd+K è DISATTIVATA (tutto il codice
 * search è commentato con il marcatore "SEARCH DISATTIVATO"). Per riattivarla
 * cercare quel marcatore nel file e scommentare i blocchi coinvolti.
 */
import {
  Alert,
  Box,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AppsIcon from "@mui/icons-material/Apps";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyIcon from "@mui/icons-material/Key";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SpeedIcon from "@mui/icons-material/Speed";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useCallback, useEffect, useMemo, useState } from "react";
// SEARCH DISATTIVATO — useRef (rimosso dall'import sopra) serve solo per la
// ref del campo search: riaggiungerlo riattivando la search.
import { Fragment } from "react";
import { useTranslations } from "hooks";
import { useSnackbar } from "hooks";
import { useUser } from "hooks";
import { getTranslation } from "common/utilities";
import {
  CODE_LANGS,
  CodeLang,
  DEFAULT_SCENARIO,
  ROBOTS_HELP_CODE_SNIPPETS,
  ROBOTS_HELP_SCENARIOS,
  ScenarioKey,
} from "./robotsHelpCodeSnippets";
import { getRobotsHelpTranslations } from "./robotsHelpDict";
import {
  AccessLevel,
  EndpointCardData,
  ENDPOINTS_BY_CATEGORY,
  HttpMethod,
  ROBOTS_HELP_ENDPOINTS_BY_ID,
} from "./robotsHelpEndpoints";

// ────────────────────────────────────────────────────────────────────────
// Design tokens MD3 (palette ispirata a public/robots-help.html tailwind config)
// ────────────────────────────────────────────────────────────────────────

const METHOD_BG: Record<HttpMethod, string> = {
  GET: "#0ea5e9", // sky-500
  POST: "#10b981", // emerald-500
  PUT: "#f59e0b", // amber-500
  DELETE: "#ef4444", // red-500
};

/** Badge per distinguere a colpo d'occhio l'accessibilità robot di un endpoint. */
const ACCESS_BADGE: Record<
  AccessLevel,
  { bg: string; labelKey: string; emoji: string }
> = {
  "whitelist": {
    bg: "#16a34a", // green-600
    labelKey: "robots.help.badge.whitelist",
    emoji: "✓",
  },
  "requires-whitelist": {
    bg: "#f59e0b", // amber-500
    labelKey: "robots.help.badge.requires",
    emoji: "⚠",
  },
};

const CAT_COLORS: Record<string, string> = {
  cat1: "#0ea5e9", // sky
  cat2: "#10b981", // emerald
  cat3: "#f59e0b", // amber
  cat4: "#a855f7", // purple
  cat5: "#6366f1", // indigo
  cat6: "#f43f5e", // rose
  cat7: "#14b8a6", // teal
  cat9: "#3b82f6", // blue
};

const CANONICAL_URL = "https://www.flowenti.com/dashboard/robots/help";

const JSON_LD_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "TechArticle",
  headline: "Flowenti Robots API",
  description:
    "Documentazione completa sui 15 endpoint REST di Flowenti Robots API: autenticazione, polling task, token state, gateway e allegati.",
  url: CANONICAL_URL,
  inLanguage: ["it", "en", "fr", "de", "es", "pt"],
  author: {
    "@type": "Organization",
    name: "Flowenti",
    url: "https://www.flowenti.com/",
  },
  audience: {
    "@type": "Audience",
    audienceType: "Developers",
  },
  proficiencyLevel: "Expert",
};

/** Status code HTTP documentati nella tabella "Errors". Single source of truth:
 *  il badge count nella sidebar è derivato da `HTTP_ERROR_CODES.length`. */
const HTTP_ERROR_CODES = [
  { code: "400", color: "#f59e0b" },
  { code: "401", color: "#f43f5e" },
  { code: "403", color: "#f43f5e" },
  { code: "404", color: "#a855f7" },
  { code: "409", color: "#f59e0b" },
  { code: "429", color: "#6366f1" },
  { code: "500", color: "#f43f5e" },
] as const;

// ────────────────────────────────────────────────────────────────────────
// Tipi locali
// ────────────────────────────────────────────────────────────────────────

type CategoryId =
  | "cat1"
  | "cat2"
  | "cat3"
  | "cat4"
  | "cat5"
  | "cat6"
  | "cat7"
  | "cat9"
  | "errors";

interface CategoryDef {
  id: CategoryId;
  icon: JSX.Element;
  labelKey: string;
  count: number;
}

// ────────────────────────────────────────────────────────────────────────
// Helpers riusabili
// ────────────────────────────────────────────────────────────────────────

/** Inietta (e rimuove a unmount) il JSON-LD TechArticle nel <head>. */
const useJsonLd = () => {
  useEffect(() => {
    const id = "robots-help-jsonld";
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify(JSON_LD_SCHEMA);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, []);
};

/** Inietta (e rimuove a unmount) gli stessi Google Fonts delle pagine
 *  statiche `robots-help-{lang}.html` (Inter + JetBrains Mono): la pagina
 *  React ha così la STESSA identica tipografia della versione statica,
 *  senza toccare il tema Poppins del resto del dashboard. */
const GOOGLE_FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap";

const usePageFonts = () => {
  useEffect(() => {
    const preconnects: HTMLLinkElement[] = [];
    ["https://fonts.googleapis.com", "https://fonts.gstatic.com"].forEach(
      (href, i) => {
        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = href;
        if (i === 1) link.crossOrigin = "anonymous";
        document.head.appendChild(link);
        preconnects.push(link);
      }
    );
    const stylesheet = document.createElement("link");
    stylesheet.rel = "stylesheet";
    stylesheet.href = GOOGLE_FONTS_HREF;
    document.head.appendChild(stylesheet);
    return () => {
      preconnects.forEach((l) => l.remove());
      stylesheet.remove();
    };
  }, []);
};

/** Inietta (e rimuove a unmount) il CSS di colorazione sintattica dei
 *  token degli snippet del code hub. Stessa identica palette delle pagine
 *  statiche `robots-help-{lang}.html` (blocco `.token.*` nel <head>): gli
 *  snippet contengono già markup `<span class="token …">` — senza questo
 *  stile il codice del hub appariva monocromatico e illeggibile. */
const TOKEN_CSS = `
.token.keyword { color: #c678dd; font-weight: 600; }
.token.string { color: #98c379; }
.token.function { color: #61afef; }
.token.number { color: #d19a66; }
.token.operator { color: #56b6c2; }
.token.comment { color: #7f848e; font-style: italic; }
.token.property { color: #e06c75; }
`;

const useTokenStyles = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "robots-help-token-css";
    style.textContent = TOKEN_CSS;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);
};

// SEARCH DISATTIVATO — hook Cmd+K/Ctrl+K focus search input. Riattivare
// insieme al campo search nella sidebar.
// const useCmdK = (searchRef: React.RefObject<HTMLInputElement>) => {
//   useEffect(() => {
//     const handler = (e: KeyboardEvent) => {
//       if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
//         e.preventDefault();
//         searchRef.current?.focus();
//       }
//     };
//     window.addEventListener("keydown", handler);
//     return () => window.removeEventListener("keydown", handler);
//   }, [searchRef]);
// };

// ────────────────────────────────────────────────────────────────────────
// EndpointCard riusabile
// ────────────────────────────────────────────────────────────────────────

interface EndpointCardProps {
  data: EndpointCardData;
  t: (key: string) => string;
  palette: Palette;
  onCopy: (text: string, label: string) => void;
}

interface Palette {
  sidebar: string;
  card: string;
  border: string;
  muted: string;
  code: string;
  accent: string;
  primary: string;
  /** Chip `<code>` inline (legende e path delle card) — colori identici alle
   *  statiche: bg-gray-100/dark:bg-gray-800, testo navy / dark teal-300. */
  codeChipBg: string;
  codeChipText: string;
}

const EndpointCard = ({ data, t, palette, onCopy }: EndpointCardProps) => {
  const codeSx = {
    p: 1.5,
    bgcolor: palette.code,
    color: "#e4e4e7",
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 13,
    borderRadius: 1.5,
    overflow: "auto" as const,
    m: 0,
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
  };

  return (
    <Paper
      id={data.anchorId}
      variant="outlined"
      sx={{
        p: 2.5,
        mb: 1.5,
        borderRadius: 3,
        bgcolor: palette.card,
        borderColor: palette.border,
        scrollMarginTop: 96,
      }}
    >
      {/* Header: metodo + path + copy */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 1.5,
            bgcolor: METHOD_BG[data.method],
            color: "#fff",
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.5,
            minWidth: 56,
            textAlign: "center",
          }}
        >
          {data.method}
        </Box>
        <Tooltip title={t(ACCESS_BADGE[data.accessLevel].labelKey)}>
          <Box
            aria-label={t(ACCESS_BADGE[data.accessLevel].labelKey)}
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: ACCESS_BADGE[data.accessLevel].bg,
              color: "#fff",
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: 0.3,
              whiteSpace: "nowrap",
            }}
          >
            {ACCESS_BADGE[data.accessLevel].emoji} {t(ACCESS_BADGE[data.accessLevel].labelKey)}
          </Box>
        </Tooltip>
        <Box
          component="code"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 15,
            fontWeight: 700,
            color: palette.codeChipText,
            wordBreak: "break-all",
            flex: 1,
          }}
        >
          {data.path}
        </Box>
        <Tooltip title={t("robots.help.examples.copytitle")}>
          <IconButton
            size="small"
            onClick={() => onCopy(data.path, data.anchorId)}
            aria-label="copy path"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Descrizione lunga (i18n) */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t(data.descKey)}
      </Typography>

      {/* Request example (solo se presente) */}
      {data.requestExample && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: palette.muted,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Request Body
          </Typography>
          <Paper variant="outlined" sx={codeSx}>
            <pre style={{ margin: 0 }}>{data.requestExample}</pre>
          </Paper>
          {/* Legenda dei campi del body (2026-09-10): spiegazione di natura e
              funzionamento di ogni campo del payload, subito dopo l'esempio. */}
          {data.bodyLegend && data.bodyLegend.length > 0 && (
            <Box
              component="ul"
              sx={{
                mt: 1,
                pl: 2.5,
                m: 0,
                listStyle: "disc",
                color: "text.secondary",
              }}
            >
              {data.bodyLegend.map((entry) => (
                <Typography
                  key={entry.name}
                  component="li"
                  variant="caption"
                  sx={{ display: "list-item", mb: 0.25, fontSize: 13 }}
                >
                  <Box
                    component="code"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 700,
                      fontSize: 12,
                      bgcolor: palette.codeChipBg,
                      color: palette.codeChipText,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 1,
                      mr: 1,
                    }}
                  >
                    {entry.name}
                  </Box>
                  {t(entry.descKey)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Legenda dei parametri di path/query (2026-09-11, endpoint GET):
          spiegazione di ogni parametro, stesso stile della legenda del body. */}
      {data.paramLegend && data.paramLegend.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: palette.muted,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Path Parameters
          </Typography>
          <Box
            component="ul"
            sx={{
              mt: 1,
              pl: 2.5,
              m: 0,
              listStyle: "disc",
              color: "text.secondary",
            }}
          >
            {data.paramLegend.map((entry) => (
              <Typography
                key={entry.name}
                component="li"
                variant="caption"
                sx={{ display: "list-item", mb: 0.25, fontSize: 13 }}
              >
                <Box
                  component="code"
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: palette.codeChipBg,
                    color: palette.codeChipText,
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 1,
                    mr: 1,
                  }}
                >
                  {entry.name}
                  {entry.isQuery ? " (query)" : ""}
                </Box>
                {t(entry.descKey)}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {/* Response example */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: palette.muted,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Response 200 OK
        </Typography>
        <Paper variant="outlined" sx={codeSx}>
          <pre style={{ margin: 0 }}>{data.responseExample}</pre>
        </Paper>
        {/* Legenda dei campi della response (2026-09-11): spiegazione di
            ogni dato restituito, stesso stile della legenda del body. */}
        {data.responseLegend && data.responseLegend.length > 0 && (
          <Box
            component="ul"
            sx={{
              mt: 1,
              pl: 2.5,
              m: 0,
              listStyle: "disc",
              color: "text.secondary",
            }}
          >
            {data.responseLegend.map((entry) => (
              <Typography
                key={entry.name}
                component="li"
                variant="caption"
                sx={{ display: "list-item", mb: 0.25, fontSize: 13 }}
              >
                <Box
                  component="code"
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: palette.codeChipBg,
                    color: palette.codeChipText,
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 1,
                    mr: 1,
                  }}
                >
                  {entry.name}
                </Box>
                {t(entry.descKey)}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Cell "Causa e Strategia" della tabella errori: per i codici con markup
// composto (401/404/409/429) ricostruisce la sequenza p1 + <code> + p2/p3
// del master statico (i token tecnici inline NON sono tradotti).
// ────────────────────────────────────────────────────────────────────────

/** Segmenti `<code>` inline per codice, nell'ordine del master statico.
 *  `trailing` è la punteggiatura finale hardcoded dopo l'ultimo segmento
 *  (es. il punto della riga 409). Le chiavi p2/p3 mancanti nel dict NON
 *  vengono renderizzate (il test `val !== key` scarta la chiave grezza). */
const ERROR_STRATEGY_CODES: Record<string, { segments: string[]; trailing?: string }> = {
  "401": { segments: ["Authorization: Bearer <JWT>"] },
  "409": { segments: ["/doing"], trailing: "." },
  "429": { segments: ["/tasks/organizations/{id}/canget"] },
};

const ErrorStrategyCell = ({ code, t }: { code: string; t: (key: string) => string }) => {
  const spec = ERROR_STRATEGY_CODES[code];
  if (!spec) return <>{t(`robots.help.errors.${code}.strategy`)}</>;
  return (
    <>
      <span>{t(`robots.help.errors.${code}.strategy.p1`)}</span>
      {spec.segments.map((seg, i) => {
        const nextKey = `robots.help.errors.${code}.strategy.p${i + 2}`;
        const nextVal = t(nextKey);
        return (
          <Fragment key={`seg${i}`}>
            <code
              style={{
                background: "rgba(0,0,0,0.1)",
                padding: "0 4px",
                borderRadius: 3,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 13,
              }}
            >
              {seg}
            </code>
            {nextVal !== nextKey && <span>{nextVal}</span>}
          </Fragment>
        );
      })}
      {spec.trailing}
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Componente principale
// ────────────────────────────────────────────────────────────────────────

const RobotsHelp = () => {
  const theme = useTheme();
  const { sendSnack } = useSnackbar();

  // Lingua impostata dall'utente su Flowenti (campo `i18n` dell'utente
  // autenticato, popolato da AuthContext). È la sorgente canonica per le
  // pagine interne: NON leggere `navigator.language` qui dentro (la pagina
  // è dentro /dashboard ed è sempre servita ad un utente loggato).
  // Vedi pattern canonico in `App.tsx` → `user?.i18n`.
  const user = useUser();

  // i18n canonico: useTranslations → merge offline → getTranslation
  const storeTranslations = useTranslations();
  const translations = useMemo(
    () => getRobotsHelpTranslations(storeTranslations, user?.i18n || ""),
    [storeTranslations, user?.i18n]
  );
  const t = (key: string) => getTranslation(translations, key);

  /** t con interpolazione: tf("k", { count: 16 }) → "Taxonomie API (16 endpoints)".
   *  `getTranslation` resta semplice (no params) per non rompere gli altri ~250
   *  call site. Questa è la variante dedicata alle stringhe parametrizzate. */
  const tf = useCallback(
    (key: string, params: Record<string, string | number>) => {
      const raw = getTranslation(translations, key);
      return Object.entries(params).reduce(
        (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)),
        raw
      );
    },
    [translations]
  );

  // JSON-LD SEO + font statici (stessa tipografia delle pagine statiche)
  useJsonLd();
  usePageFonts();
  // Colorazione sintattica dei token del code hub (stessa palette statiche)
  useTokenStyles();
  // SEARCH DISATTIVATO — ref + shortcut Cmd+K per il campo search.
  // const searchRef = useRef<HTMLInputElement>(null);
  // useCmdK(searchRef);

  // ────── State ──────
  // SEARCH DISATTIVATO — query della live filter.
  // const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<CategoryId | null>(null);
  const [scenario, setScenario] = useState<ScenarioKey>(DEFAULT_SCENARIO);
  const [codeLang, setCodeLang] = useState<CodeLang>("python");
  const [codeCopied, setCodeCopied] = useState(false);

  // ────── Palette MD3 ──────
  const palette = useMemo<Palette>(() => {
    const dark = theme.palette.mode === "dark";
    return {
      sidebar: dark ? "#1e293b" : "#eef3f6",
      card: dark ? "#1e293b" : "#ffffff",
      border: dark ? "#334155" : "#e2e8f0",
      muted: dark ? "#94a3b8" : "#64748b",
      code: "#1e1e1e",
      accent: dark ? "#22d3ee" : "#00a896",
      primary: dark ? "#38bdf8" : "#0b2545",
      // Identici alle classi statiche bg-gray-100/dark:bg-gray-800 e
      // text-flowenti-navy/dark:text-teal-300 dei chip <code> inline.
      codeChipBg: dark ? "#1f2937" : "#f3f4f6",
      codeChipText: dark ? "#5eead4" : "#0b2545",
    };
  }, [theme]);

  // ────── Conteggi runtime ──────
  // Sorgente canonica: ENDPOINTS_BY_CATEGORY (robotsHelpEndpoints.ts).
  // Niente numeri cablati: aggiungere/rimuovere un endpoint aggiorna automaticamente
  // il badge della sidebar, il chip count, e tutte le stringhe i18n parametrizzate.
  const totalEndpoints = useMemo(
    () =>
      Object.values(ENDPOINTS_BY_CATEGORY).reduce((sum, ids) => sum + ids.length, 0),
    []
  );

  // ────── Categorie (cat1..cat9 + errors) ──────
  // Il `count` di ogni categoria è derivato dalla rispettiva fonte canonica,
  // MAI cablato: cat1..cat9 → ENDPOINTS_BY_CATEGORY[c.id].length,
  // errors → HTTP_ERROR_CODES.length.
  const categories = useMemo<CategoryDef[]>(
    () => [
      { id: "cat1", icon: <KeyIcon fontSize="small" />, labelKey: "robots.help.sidebar.cat1", count: (ENDPOINTS_BY_CATEGORY.cat1 ?? []).length },
      { id: "cat2", icon: <PlayCircleIcon fontSize="small" />, labelKey: "robots.help.sidebar.cat2", count: (ENDPOINTS_BY_CATEGORY.cat2 ?? []).length },
      { id: "cat3", icon: <SearchIcon fontSize="small" />, labelKey: "robots.help.sidebar.cat3", count: (ENDPOINTS_BY_CATEGORY.cat3 ?? []).length },
      { id: "cat4", icon: <PlayCircleIcon fontSize="small" />, labelKey: "robots.help.sidebar.cat4", count: (ENDPOINTS_BY_CATEGORY.cat4 ?? []).length },
      { id: "cat5", icon: <AltRouteIcon fontSize="small" />, labelKey: "robots.help.sidebar.cat5", count: (ENDPOINTS_BY_CATEGORY.cat5 ?? []).length },
      { id: "cat6", icon: <AttachFileIcon fontSize="small" />, labelKey: "robots.help.sidebar.cat6", count: (ENDPOINTS_BY_CATEGORY.cat6 ?? []).length },
      { id: "cat7", icon: <PlaylistPlayIcon fontSize="small" />, labelKey: "robots.help.sidebar.cat7", count: (ENDPOINTS_BY_CATEGORY.cat7 ?? []).length },
      { id: "cat9", icon: <TravelExploreIcon fontSize="small" />, labelKey: "robots.help.sidebar.cat9", count: (ENDPOINTS_BY_CATEGORY.cat9 ?? []).length },
      { id: "errors", icon: <WarningAmberIcon fontSize="small" />, labelKey: "robots.help.sidebar.errors", count: HTTP_ERROR_CODES.length },
    ],
    []
  );

  // ────── Filtro search live ──────
  // SEARCH DISATTIVATO — live filter sulla query. Con la search off mostriamo
  // sempre tutti gli endpoint di ogni categoria.
  // const filteredEndpointsByCategory = useMemo(() => {
  //   const q = searchQuery.toLowerCase().trim();
  //   if (!q) return ENDPOINTS_BY_CATEGORY;
  //   const out: Record<string, string[]> = {};
  //   for (const [catId, anchors] of Object.entries(ENDPOINTS_BY_CATEGORY)) {
  //     const matched = anchors.filter((aid) => {
  //       const ep = ROBOTS_HELP_ENDPOINTS_BY_ID[aid];
  //       if (!ep) return false;
  //       const desc = t(ep.descKey).toLowerCase();
  //       return (
  //         ep.path.toLowerCase().includes(q) ||
  //         ep.anchorId.toLowerCase().includes(q) ||
  //         desc.includes(q)
  //       );
  //     });
  //     if (matched.length) out[catId] = matched;
  //   }
  //   return out;
  // }, [searchQuery, t]);

  const filteredEndpointsByCategory = ENDPOINTS_BY_CATEGORY;

  // SEARCH DISATTIVATO — empty state della search.
  // const hasSearchResults =
  //   !searchQuery || Object.keys(filteredEndpointsByCategory).length > 0;

  // ────── Handlers ──────
  const handleCopy = useCallback(
    (text: string, label: string) => {
      navigator.clipboard
        .writeText(text)
        .then(() => sendSnack({ type: "success", message: t("robots.help.toast.copied") }))
        .catch(() => sendSnack({ type: "error", message: t("robots.help.toast.copyfailed") }));
    },
    [sendSnack, t]
  );

  const handleChipClick = useCallback((catId: CategoryId) => {
    setActiveCategory((prev) => (prev === catId ? null : catId));
    // Smooth scroll verso la sezione
    window.setTimeout(() => {
      document.getElementById(catId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const handleFilterChip = useCallback((catId: CategoryId) => {
    setActiveCategory((prev) => (prev === catId ? null : catId));
  }, []);

  const handleCopyCode = useCallback(async () => {
    const raw = ROBOTS_HELP_CODE_SNIPPETS[scenario]?.[codeLang] ?? "";
    // Gli snippet TypeScript sono memorizzati come string[] (via helper T(...));
    // Python/Delphi sono già stringa. Normalizziamo prima di strippare i tag HTML.
    const snippet = Array.isArray(raw) ? raw.join("\n") : raw;
    const plain = snippet.replace(/<[^>]+>/g, "");
    try {
      await navigator.clipboard.writeText(plain);
      setCodeCopied(true);
      sendSnack({ type: "success", message: t("robots.help.toast.copied") });
      window.setTimeout(() => setCodeCopied(false), 1500);
    } catch {
      sendSnack({ type: "error", message: t("robots.help.toast.copyfailed") });
    }
  }, [scenario, codeLang, sendSnack, t]);

  const codeHtml = useMemo(() => {
    const raw = ROBOTS_HELP_CODE_SNIPPETS[scenario]?.[codeLang] ?? "";
    // Gli snippet TypeScript sono string[] (helper T(...)); Python/Delphi sono stringa.
    // dangerouslySetInnerHTML richiede una stringa, quindi normalizziamo.
    return Array.isArray(raw) ? raw.join("\n") : raw;
  }, [scenario, codeLang]);

  // ────── Visible category list (search + active filter combinati) ──────
  const visibleCategories = useMemo(() => {
    return categories.filter((c) => {
      // SEARCH DISATTIVATO — filtro per query della search.
      // if (searchQuery && !filteredEndpointsByCategory[c.id]) return false;
      if (activeCategory && activeCategory !== c.id) return false;
      return true;
    });
  }, [categories, activeCategory]);

  // ────── Render ──────
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        py: 2,
        fontFamily: '"Inter", sans-serif',
        // Tipografia uniforme con le pagine statiche `robots-help-{lang}.html`:
        // il tema MUI applica Poppins alle varianti Typography e ai Chip (label);
        // qui la pagina usa Inter ovunque, come le statiche.
        "& .MuiTypography-root": { fontFamily: '"Inter", sans-serif' },
        "& .MuiChip-label": { fontFamily: '"Inter", sans-serif' },
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ minHeight: "calc(100vh - 96px)" }}>
        {/* ============ SIDEBAR ============ */}
        <Paper
          elevation={0}
          sx={{
            width: { xs: "100%", md: 288 },
            flexShrink: 0,
            bgcolor: palette.sidebar,
            borderRadius: 3,
            p: 2,
            position: { md: "sticky" },
            top: { md: 16 },
            alignSelf: { md: "flex-start" },
            maxHeight: { md: "calc(100vh - 112px)" },
            overflowY: { md: "auto" },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: palette.accent + "22",
                display: "grid",
                placeItems: "center",
              }}
            >
              <SmartToyIcon sx={{ color: palette.accent }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: 15 }}>
                {t("robots.help.sidebar.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                {t("robots.help.sidebar.subtitle")}
              </Typography>
            </Box>
          </Stack>

          {/* SEARCH DISATTIVATO — campo search con hint Cmd+K.
              Riattivare insieme a: useCmdK, searchRef, searchQuery,
              filteredEndpointsByCategory, hasSearchResults, chip "Tutti". */}
          {/* <TextField
            inputRef={searchRef}
            fullWidth
            size="small"
            placeholder={tf("robots.help.search.placeholder", { count: totalEndpoints })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: palette.muted }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box
                    component="kbd"
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      fontSize: 10,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: palette.muted,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      display: { xs: "none", md: "inline-block" },
                    }}
                  >
                    ⌘K
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                bgcolor: palette.card,
              },
            }}
          /> */}

          <Divider sx={{ mb: 1.5 }} />

          {/* Panoramica & API Key (come sulle statiche: voce riempita navy) */}
          <Box
            component="a"
            href="#overview"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("overview")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.75,
              px: 2.5,
              py: 2,
              borderRadius: 2,
              textDecoration: "none",
              bgcolor: "#0b2545",
              color: "#fff",
              cursor: "pointer",
              transition: "all 0.15s",
              "&:hover": { bgcolor: "#134074" },
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
            <Typography sx={{ flex: 1, fontWeight: 600, color: "#fff", fontSize: 15 }}>
              {t("robots.help.sidebar.overview")}
            </Typography>
          </Box>

          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {/* Voce "Tutti" (SEARCH DISATTIVATO: il reset della query è sospeso).
                Come sulle statiche: item di navigazione full-width con icona apps
                e conteggio, riempita col primary #54bdc1 quando nessun filtro è
                attivo (activeCategory === null). */}
            {(() => {
              const active = activeCategory === null;
              return (
                <Box
                  component="a"
                  href="#overview"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveCategory(null);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                    px: 2.5,
                    py: 2,
                    borderRadius: 2,
                    textDecoration: "none",
                    color: active ? "#fff" : "text.primary",
                    bgcolor: active ? "#54bdc1" : "transparent",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": { bgcolor: active ? "#54bdc1" : "action.hover" },
                  }}
                >
                  <span style={{ color: active ? "#fff" : palette.muted }}>
                    <AppsIcon fontSize="small" />
                  </span>
                  <Typography sx={{ flex: 1, fontWeight: active ? 700 : 500, fontSize: 15 }}>
                    {t("robots.help.sidebar.all")}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontFamily: '"JetBrains Mono", monospace',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: active ? "rgba(255,255,255,0.25)" : "action.hover",
                    }}
                  >
                    {totalEndpoints}
                  </Box>
                </Box>
              );
            })()}
            {categories
              .filter((c) => c.id !== "errors")
              .map((c) => {
                const active = activeCategory === c.id;
                return (
                  <Box
                    key={c.id}
                    component="a"
                    href={`#${c.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleChipClick(c.id);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.75,
                      px: 2.5,
                      py: 2,
                      borderRadius: 2,
                      textDecoration: "none",
                      color: active ? "#fff" : "text.primary",
                      bgcolor: active ? CAT_COLORS[c.id] : "transparent",
                      cursor: "pointer",
                      transition: "all 0.15s",
                      "&:hover": { bgcolor: active ? CAT_COLORS[c.id] : "action.hover" },
                    }}
                  >
                    <span style={{ color: active ? "#fff" : CAT_COLORS[c.id] }}>{c.icon}</span>
                    <Typography sx={{ flex: 1, fontWeight: active ? 700 : 500, fontSize: 15 }}>
                      {t(c.labelKey)}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontFamily: '"JetBrains Mono", monospace',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: active ? "rgba(255,255,255,0.25)" : "action.hover",
                      }}
                    >
                      {c.count}
                    </Box>
                  </Box>
                );
              })}

            {/* Errors entry */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="overline" color="text.secondary" sx={{ px: 1, fontSize: 11 }}>
              {t("robots.help.sidebar.integration")}
            </Typography>
            <Box
              key="errors"
              component="a"
              href="#errors"
              onClick={(e) => {
                e.preventDefault();
                handleChipClick("errors");
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                px: 2.5,
                py: 2,
                borderRadius: 2,
                textDecoration: "none",
                color: activeCategory === "errors" ? "#fff" : "#f43f5e",
                bgcolor: activeCategory === "errors" ? "#f43f5e" : "transparent",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: activeCategory === "errors" ? "#f43f5e" : "action.hover",
                },
              }}
            >
              <WarningAmberIcon fontSize="small" />
              <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 15 }}>
                {t("robots.help.sidebar.errors")}
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  fontFamily: '"JetBrains Mono", monospace',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: activeCategory === "errors" ? "rgba(255,255,255,0.25)" : "#f43f5e22",
                }}
              >
                HTTP
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />
            <Box
              component="a"
              href="#examples"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("examples")?.scrollIntoView({ behavior: "smooth" });
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                px: 2.5,
                py: 1.75,
                borderRadius: 2,
                textDecoration: "none",
                color: "text.secondary",
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <PlayCircleIcon fontSize="small" />
              <Typography sx={{ fontSize: 15 }}>{t("robots.help.sidebar.examples")}</Typography>
            </Box>
          </Stack>
        </Paper>

        {/* ============ CONTENT ============ */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* HERO */}
          <Paper
            id="overview"
            elevation={0}
            sx={{
              p: 4,
              mb: 2,
              borderRadius: 4,
              bgcolor: palette.card,
              border: `1px solid ${palette.border}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -80,
                right: -80,
                width: 280,
                height: 280,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${palette.accent}33, transparent 70%)`,
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <SmartToyIcon sx={{ color: palette.accent }} fontSize="small" />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  color: palette.accent,
                }}
              >
                {t("robots.help.overview.kicker")}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                label={t("robots.help.overview.build")}
                sx={{
                  bgcolor: palette.accent + "22",
                  color: palette.accent,
                  fontWeight: 700,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 12,
                  border: `1px solid ${palette.accent}55`,
                }}
                size="small"
              />
            </Stack>
            <Typography variant="h3" fontWeight={800} sx={{ color: palette.primary, mb: 1.5, fontSize: 28 }}>
              {t("robots.help.overview.title")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
              {t("robots.help.overview.intro.p1")}
              <strong>{tf("robots.help.overview.intro.p2", { count: totalEndpoints })}</strong>
              {t("robots.help.overview.intro.p3")}
            </Typography>

            {/* Base URL + Auth header cards — auth reale: JWT Bearer con
                claims IsAPIKey='1' / RobotID (allineato agli snippet 2026-08-28
                e alle pagine statiche, 2026-09-11). */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              {[
                {
                  labelKey: "robots.help.overview.baseurl.label",
                  value: "https://<host>/flowentisysman/v1",
                  color: "#4ade80",
                },
                {
                  labelKey: "robots.help.overview.auth.label",
                  value: "Authorization: Bearer eyJhbGciOiJIUzUxMiIs...",
                  color: "#7dd3fc",
                },
              ].map((row) => (
                <Box key={row.labelKey} sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {t(row.labelKey)}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1.5,
                      bgcolor: palette.code,
                      borderRadius: 2,
                      border: `1px solid ${palette.border}`,
                    }}
                  >
                    <Box
                      component="code"
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: 13,
                        color: row.color,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.value}
                    </Box>
                    <Tooltip title={t("robots.help.examples.copytitle")}>
                      <IconButton size="small" onClick={() => handleCopy(row.value, row.labelKey)} sx={{ color: "#fff" }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
            </Stack>

            {/* API Key + Rate Limits cards */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
              <Paper
                variant="outlined"
                sx={{ flex: 1, p: 2.5, borderRadius: 3, borderColor: palette.border }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <KeyIcon fontSize="small" sx={{ color: CAT_COLORS.cat1 }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 15 }}>
                    {t("robots.help.keys.title")}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t("robots.help.keys.desc")}
                </Typography>
              </Paper>
              <Paper
                variant="outlined"
                sx={{ flex: 1, p: 2.5, borderRadius: 3, borderColor: palette.border }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <SpeedIcon fontSize="small" sx={{ color: "#f59e0b" }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 15 }}>
                    {t("robots.help.rate.title")}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t("robots.help.rate.desc")}
                </Typography>
              </Paper>
            </Stack>
          </Paper>

          {/* TAXONOMY header + filter chips */}
          <Paper
            elevation={0}
            sx={{ p: 3, mb: 2, borderRadius: 3, bgcolor: palette.card, border: `1px solid ${palette.border}` }}
          >
            <Typography variant="h5" fontWeight={700} sx={{ color: palette.primary, mb: 0.5, fontSize: 20 }}>
              {tf("robots.help.taxonomy.title", { count: totalEndpoints })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("robots.help.taxonomy.subtitle")}
            </Typography>
            <Box sx={{ overflowX: "auto", pb: 1 }}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                {categories.map((c) => (
                  <Chip
                    key={c.id}
                    label={t(
                      c.id === "errors"
                        ? "robots.help.chip.errors"
                        : `robots.help.chip.${c.id}`
                    )}
                    clickable
                    onClick={() => handleFilterChip(c.id)}
                    color={
                      activeCategory === c.id
                        ? "primary"
                        : c.id === "errors"
                        ? "error"
                        : "default"
                    }
                    variant={activeCategory === c.id ? "filled" : "outlined"}
                    sx={{
                      fontWeight: 600,
                      fontSize: 13,
                      height: 34,
                      px: 0.5,
                      borderRadius: 999,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Paper>

          {/* SEARCH DISATTIVATO — empty state se la search non trova niente */}
          {/* {!hasSearchResults && (
            <Alert severity="info" sx={{ borderRadius: 3, mb: 2 }}>
              {tf("robots.help.search.placeholder", { count: totalEndpoints })}: "{searchQuery}"
            </Alert>
          )} */}

          {/* CATEGORIES con relative endpoint card */}
          {visibleCategories
            .filter((c) => c.id !== "errors")
            .map((c) => {
              const anchors = filteredEndpointsByCategory[c.id] ?? [];
              if (anchors.length === 0) return null;
              return (
                <Fragment key={c.id}>
                <Paper
                  id={c.id}
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 3,
                    bgcolor: palette.card,
                    border: `1px solid ${palette.border}`,
                    scrollMarginTop: 96,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1, pb: 1.5, borderBottom: `1px solid ${palette.border}` }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        bgcolor: CAT_COLORS[c.id] + "22",
                        color: CAT_COLORS[c.id],
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      {c.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: palette.primary, fontSize: 18 }}>
                      {t(c.labelKey)}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Chip
                      label={`${anchors.length}`}
                      size="small"
                      sx={{
                        bgcolor: CAT_COLORS[c.id] + "22",
                        color: CAT_COLORS[c.id],
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(`robots.help.${c.id}.title`)}
                  </Typography>

                  {/* Cat3 special: Polling notice */}
                  {c.id === "cat3" && (
                    <Alert
                      icon={<InfoOutlinedIcon fontSize="small" />}
                      severity="warning"
                      sx={{ mb: 2, borderRadius: 2 }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {t("robots.help.cat3.notice.title")}
                      </Typography>
                      <Typography variant="body2">
                        {t("robots.help.cat3.notice.p1")}
                        <em>{t("robots.help.cat3.notice.p2")}</em>
                        {t("robots.help.cat3.notice.p3")}
                      </Typography>
                    </Alert>
                  )}

                  {/* Cat6 special: warning box path inconsistenza */}
                  {c.id === "cat6" && (
                    <Alert
                      icon={<WarningAmberIcon fontSize="small" />}
                      severity="error"
                      sx={{ mb: 2, borderRadius: 2 }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {t("robots.help.cat6.warning.title")}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {t("robots.help.cat6.warning.p1")}
                        <strong>{t("robots.help.cat6.warning.p2")}</strong>
                        {t("robots.help.cat6.warning.p3")}
                        <code style={{ background: "rgba(0,0,0,0.1)", padding: "0 4px", borderRadius: 3 }}>
                          /flownodes/{`{idprocesstoken}`}/attachments
                        </code>
                        {t("robots.help.cat6.warning.p4")}
                        <strong>{t("robots.help.cat6.warning.p5")}</strong>
                        {t("robots.help.cat6.warning.p6")}
                        <code style={{ background: "rgba(0,0,0,0.1)", padding: "0 4px", borderRadius: 3 }}>
                          /processes/{`{idprocess}`}/attachments/light
                        </code>
                        {t("robots.help.cat6.warning.p7")}
                        <code style={{ background: "rgba(0,0,0,0.1)", padding: "0 4px", borderRadius: 3 }}>
                          404 Not Found
                        </code>
                        .
                      </Typography>
                    </Alert>
                  )}

                  {anchors.map((aid) => {
                    const ep = ROBOTS_HELP_ENDPOINTS_BY_ID[aid];
                    if (!ep) return null;
                    return (
                      <EndpointCard
                        key={ep.anchorId}
                        data={ep}
                        t={t}
                        palette={palette}
                        onCopy={handleCopy}
                      />
                    );
                  })}

                  {/* Cat4 special: Token State Machine box */}
                  {c.id === "cat4" && (
                    <Paper
                      variant="outlined"
                      sx={{ p: 2.5, mt: 1.5, borderRadius: 3, borderColor: palette.border }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <AccountTreeIcon fontSize="small" sx={{ color: CAT_COLORS.cat4 }} />
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: 15 }}>
                          {t("robots.help.statemachine.title")}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t("robots.help.statemachine.intro")}
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, mb: 1, color: "text.secondary" }}>
                        <Typography component="li" variant="body2">
                          {t("robots.help.statemachine.state.0")}
                        </Typography>
                        <Typography component="li" variant="body2">
                          {t("robots.help.statemachine.state.1")}
                        </Typography>
                        <Typography component="li" variant="body2">
                          {t("robots.help.statemachine.state.2")}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {t("robots.help.statemachine.transitions")}
                      </Typography>
                    </Paper>
                  )}
                </Paper>
                </Fragment>
            );
            })}

          {/* ============ ERRORS ============ */}
          {(!activeCategory || activeCategory === "errors") && (
            <Paper
              id="errors"
              elevation={0}
              sx={{
                p: 3,
                mb: 2,
                borderRadius: 3,
                bgcolor: palette.card,
                border: `1px solid ${palette.border}`,
                scrollMarginTop: 96,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, pb: 1.5, borderBottom: `1px solid ${palette.border}` }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    bgcolor: "#f43f5e22",
                    color: "#f43f5e",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <WarningAmberIcon fontSize="small" />
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: "#f43f5e", fontSize: 18 }}>
                  {t("robots.help.errors.title")}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("robots.help.errors.intro")}
              </Typography>

              {/* Standard error payload */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: palette.muted, textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {t("robots.help.errors.format.label")}
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: palette.code, borderRadius: 2 }}>
                  <pre style={{ margin: 0, color: "#e4e4e7", fontFamily: '"JetBrains Mono", monospace', fontSize: 13, whiteSpace: "pre-wrap" }}>
{`{
  "error": {
    "code": "RESOURCE_TOKEN_NOT_FOUND",
    "message": "Il token 'tok_inv_123' specificato non corrisponde ad alcuna risorsa attiva.",
    "status": 404,
    "timestamp": "2026-08-24T12:00:00Z"
  }
}`}
                  </pre>
                </Paper>
              </Box>

              {/* Status code table */}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, borderColor: palette.border }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{t("robots.help.errors.th.code")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("robots.help.errors.th.state")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("robots.help.errors.th.strategy")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {HTTP_ERROR_CODES.map((row) => (
                      <TableRow key={row.code} hover>
                        <TableCell
                          sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontWeight: 700,
                            color: row.color,
                          }}
                        >
                          {row.code}
                        </TableCell>
                        <TableCell>{t(`robots.help.errors.${row.code}.state`)}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          <ErrorStrategyCell code={row.code} t={t} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* ============ EXAMPLES CODE HUB ============ */}
          <Paper
            id="examples"
            elevation={0}
            sx={{
              p: 0,
              mb: 2,
              borderRadius: 3,
              bgcolor: palette.code,
              border: `1px solid ${palette.border}`,
              overflow: "hidden",
              scrollMarginTop: 96,
            }}
          >
            {/* Header */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 3, py: 2, bgcolor: palette.card, borderBottom: `1px solid ${palette.border}` }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <PlayCircleIcon sx={{ color: palette.accent }} />
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: 20 }}>
                  {t("robots.help.examples.title")}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 13 }}>
                {tf("robots.help.examples.subtitle", { count: totalEndpoints })}
              </Typography>
            </Stack>

            {/* Scenario tabs */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: palette.card, borderBottom: `1px solid ${palette.border}`, overflowX: "auto" }}>
              <Stack direction="row" spacing={1}>
                {ROBOTS_HELP_SCENARIOS.map((s) => (
                  <Chip
                    key={s.key}
                    label={t(`robots.help.examples.${s.scenKey}`)}
                    clickable
                    onClick={() => setScenario(s.key)}
                    color={scenario === s.key ? "primary" : "default"}
                    variant={scenario === s.key ? "filled" : "outlined"}
                    sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 13, height: 32 }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Language tabs + copy */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 1, bgcolor: palette.code, borderBottom: `1px solid ${palette.border}` }}>
              <Stack direction="row" spacing={3}>
                {CODE_LANGS.map((l) => (
                  <Box
                    key={l.key}
                    onClick={() => setCodeLang(l.key)}
                    sx={{
                      cursor: "pointer",
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 13,
                      pb: 0.5,
                      borderBottom: 2,
                      borderColor: codeLang === l.key ? palette.accent : "transparent",
                      color: codeLang === l.key ? palette.accent : "#94a3b8",
                      fontWeight: codeLang === l.key ? 700 : 400,
                      "&:hover": { color: codeLang === l.key ? palette.accent : "#e4e4e7" },
                    }}
                  >
                    {l.key === "python" ? "Python" : t(l.labelKey)}
                  </Box>
                ))}
              </Stack>
              <Tooltip title={codeCopied ? "OK" : t("robots.help.examples.copytitle")}>
                <IconButton size="small" onClick={handleCopyCode} sx={{ color: "#e4e4e7" }} aria-label="copy code">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Code block */}
            <Box sx={{ p: 2.5, position: "relative" }}>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 13,
                  color: "#e4e4e7",
                  whiteSpace: "pre",
                  overflowX: "auto",
                  maxHeight: 480,
                }}
                dangerouslySetInnerHTML={{ __html: codeHtml }}
              />
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Container>
  );
};

export default RobotsHelp;
